import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import { ChatType } from '../../../../shared/types/monitor/ChatType';
import { useGetDomainsInfinite } from '../../../../shared/api/hooks/monitor/data/sources/UseGetDomainsInfinite';

interface DomainsFilterProps {
  projectId: number;
  chatType: ChatType;
  from: Date;
  to: Date;
  selectedDomains: string[];
  onChange: (selectedDomains: string[]) => void;
}

interface DomainOption {
  name: string;
  domain: string;
}

const MAX_SELECTION_LIMIT = 10;

const DomainsFilter: React.FC<DomainsFilterProps> = ({
  projectId,
  chatType,
  from,
  to,
  selectedDomains,
  onChange,
}) => {
  const { t } = useTranslation();
  const [selectedItems, setSelectedItems] = useState<DomainOption[] | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [filterValue, setFilterValue] = useState<string>('');

  const multiSelectRef = useRef<MultiSelect>(null);

  const { allOptions, allLoadedDomains, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetDomainsInfinite(projectId, chatType, from, to, undefined);

  useEffect(() => {
    if (allLoadedDomains.length === 0 || selectedItems !== null) {
      return;
    }

    const loadInitialSelection = async () => {
      const selected = allOptions.filter((option) => selectedDomains.includes(option.domain));
      setSelectedItems(selected);
    };

    loadInitialSelection();
  }, [allLoadedDomains.length, selectedItems, allOptions, selectedDomains, projectId]);

  useEffect(() => {
    if (!isPanelOpen) {
      return;
    }

    let observer: IntersectionObserver | null = null;

    const setupObserver = () => {
      const panel = document.querySelector('[data-pc-section="panel"] [data-pc-section="wrapper"]');
      if (!panel) {
        return;
      }

      const sentinel = document.createElement('div');
      sentinel.setAttribute('data-sentinel', 'true');
      sentinel.style.height = '1px';
      panel.appendChild(sentinel);

      observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting && hasNextPage && !isFetchingNextPage && !filterValue) {
            fetchNextPage();
          }
        },
        {
          root: panel,
          rootMargin: '200px',
          threshold: 0,
        }
      );

      observer.observe(sentinel);

      return () => {
        observer?.disconnect();
        sentinel.remove();
      };
    };

    const timeout1 = setTimeout(setupObserver, 100);
    const timeout2 = setTimeout(setupObserver, 300);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      observer?.disconnect();
      const sentinel = document.querySelector('[data-sentinel="true"]');
      sentinel?.remove();
    };
  }, [isPanelOpen, hasNextPage, isFetchingNextPage, fetchNextPage, filterValue]);

  const handleChange = useCallback(
    (e: any) => {
      const updated = e.value || [];

      if (updated.length > MAX_SELECTION_LIMIT) {
        const limited = updated.slice(0, MAX_SELECTION_LIMIT);
        setSelectedItems(limited);
        onChange(limited.map((option: DomainOption) => option.domain));
        return;
      }

      setSelectedItems(updated);
      onChange(updated.map((option: DomainOption) => option.domain));
    },
    [onChange]
  );

  const itemTemplate = useCallback(
    (option: DomainOption) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span
          style={{
            backgroundImage: `url("https://favicon.is/${option.domain}")`,
            width: '16px',
            height: '16px',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
          }}
        />
        <span>{option.name}</span>
      </div>
    ),
    []
  );

  return (
    <MultiSelect
      ref={multiSelectRef}
      value={selectedItems}
      onChange={handleChange}
      options={allOptions}
      className="dropdown dropdown--multi-select"
      optionLabel="name"
      itemTemplate={itemTemplate}
      maxSelectedLabels={1}
      selectionLimit={MAX_SELECTION_LIMIT}
      showSelectAll={false}
      placeholder={t('selectDomains')}
      filter
      filterBy="name"
      onFilter={(e) => setFilterValue(e.filter)}
      onShow={() => setIsPanelOpen(true)}
      onHide={() => {
        setIsPanelOpen(false);
        setFilterValue('');
      }}
      panelFooterTemplate={() => (
        <div className="domains-filter__footer">
          {isFetchingNextPage && <div className="p-multiselect-footer__loader" />}
          <Button
            label={t('resetToDefault')}
            className="btn btn--sm btn--primary"
            onClick={() => {
              setSelectedItems([]);
              multiSelectRef.current?.hide();
              onChange([]);
            }}
            disabled={!selectedItems || selectedItems.length === 0}
          />
        </div>
      )}
    />
  );
};

export default DomainsFilter;
